import { useEffect, useId, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { brandAsset } from "@mashriqain/brand";
import { Icon, type IconName } from "./icons";
import { Avatar } from "./kit";
import { get, identity, type Account, type Me, type Overview } from "../lib/api";

interface SubItem {
  to: string;
  label: string;
  /** Whether this is the page shown, from the path and its query. */
  current: (path: string, query: URLSearchParams) => boolean;
  count?: (o: Overview) => number;
}

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** The section this item opens, for highlighting it on the pages inside. */
  section: (path: string) => boolean;
  count?: (o: Overview) => number;
  /** A shortcut to creating one, shown on hover beside the item. */
  add?: { to: string; label: string };
  children?: SubItem[];
}

const inAccounts = (p: string) => p === "/accounts" || p.startsWith("/accounts/");
const inPortals = (p: string) => p === "/portals" || p.startsWith("/portals/") || p === "/connect";

const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: "Manage",
    items: [
      { to: "/", label: "Overview", icon: "overview", section: (p) => p === "/" },
      {
        to: "/accounts",
        label: "Accounts",
        icon: "accounts",
        section: inAccounts,
        count: (o) => o.accounts.total,
        add: { to: "/accounts?new=1", label: "New account" },
        children: [
          { to: "/accounts", label: "Everyone", current: (p, q) => p === "/accounts" && !q.get("filter"), count: (o) => o.accounts.total },
          {
            to: "/accounts?filter=admins",
            label: "Administrators",
            current: (p, q) => p === "/accounts" && q.get("filter") === "admins",
            count: (o) => o.accounts.admins,
          },
          {
            to: "/accounts?filter=disabled",
            label: "Disabled",
            current: (p, q) => p === "/accounts" && q.get("filter") === "disabled",
            count: (o) => o.accounts.disabled,
          },
        ],
      },
      {
        to: "/portals",
        label: "Portals",
        icon: "portals",
        section: inPortals,
        count: (o) => o.clients.active,
        add: { to: "/portals/new", label: "Register a portal" },
        children: [
          { to: "/portals", label: "All portals", current: (p) => p === "/portals", count: (o) => o.clients.total },
          { to: "/portals/new", label: "Register a portal", current: (p) => p === "/portals/new" },
          { to: "/connect", label: "Connect an app", current: (p) => p === "/connect" },
        ],
      },
    ],
  },
  {
    group: "System",
    items: [
      { to: "/audit", label: "Audit log", icon: "audit", section: (p) => p === "/audit" },
      { to: "/settings", label: "Configuration", icon: "settings", section: (p) => p === "/settings" },
    ],
  },
];

/** Pages the search box can jump to, matched on their names. */
const JUMPS: { to: string; label: string; icon: IconName }[] = [
  { to: "/", label: "Overview", icon: "overview" },
  { to: "/accounts", label: "Accounts", icon: "accounts" },
  { to: "/accounts?new=1", label: "New account", icon: "plus" },
  { to: "/portals", label: "Portals", icon: "portals" },
  { to: "/portals/new", label: "Register a portal", icon: "plus" },
  { to: "/connect", label: "Connect an app", icon: "key" },
  { to: "/audit", label: "Audit log", icon: "audit" },
  { to: "/settings", label: "Configuration", icon: "settings" },
];

const GROUP_SITES = [
  { href: "https://id.mashriqain.com/", label: "Mashriqain ID", note: "id.mashriqain.com", icon: "shield" as const },
  { href: "https://mashriqain.com/", label: "mashriqain.com", note: "The group's website", icon: "portals" as const },
];

/** A menu that closes on a click elsewhere or on Escape. */
function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      ref.current?.querySelector<HTMLButtonElement>("[aria-haspopup]")?.focus();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

interface SidebarProps {
  me: Me;
  overview?: Overview;
  collapsed: boolean;
  searchRef: RefObject<HTMLInputElement | null>;
  onToggle: () => void;
  onClose: () => void;
  /** Open the sidebar wide and put the cursor in the search box. */
  onSearch: () => void;
  onSignOut: () => void;
}

/** The panel's navigation: the group switcher, search, sections and the signed-in administrator. */
export function Sidebar({ me, overview, collapsed, searchRef, onToggle, onClose, onSearch, onSignOut }: SidebarProps) {
  const { pathname, search } = useLocation();
  const query = new URLSearchParams(search);

  return (
    <aside id="ops-sidebar" className="sidebar" aria-label="Ops panel">
      <div className="sidebar-brand">
        <NavLink to="/" end className="sidebar-home" aria-label="Mashriqain Ops, overview">
          <img src={brandAsset("logo-horizontal-ivory.svg")} alt="" className="sidebar-logo" width={979} height={210} />
          <img src={brandAsset("mark-ivory.svg")} alt="" className="sidebar-mark" width={200} height={200} />
        </NavLink>
        <button type="button" className="sidebar-close" aria-label="Close menu" onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      <button
        type="button"
        className="sidebar-toggle"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-pressed={collapsed}
        aria-controls="ops-sidebar"
        data-tip={collapsed ? "Expand  [" : "Collapse  ["}
        onClick={onToggle}
      >
        <Icon name="sidebar" size={14} />
      </button>

      <Switcher />
      <QuickFind inputRef={searchRef} onSearch={onSearch} />

      <nav className="sidebar-nav" aria-label="Sections">
        {NAV.map((g) => (
          <div key={g.group} className="nav-group">
            <p className="nav-group-label">{g.group}</p>
            <ul role="list">
              {g.items.map((item) => {
                const active = item.section(pathname);
                const count = item.count && overview ? item.count(overview) : undefined;
                return (
                  <li key={item.to} className="nav-item">
                    <Link
                      to={item.to}
                      className="nav-link"
                      data-active={active || undefined}
                      aria-current={pathname === item.to && !search ? "page" : undefined}
                      data-tip={item.label}
                    >
                      <Icon name={item.icon} />
                      <span className="nav-label">{item.label}</span>
                      {count !== undefined && <span className="nav-count">{count}</span>}
                    </Link>
                    {item.add && (
                      <Link to={item.add.to} className="nav-add" aria-label={item.add.label} title={item.add.label}>
                        <Icon name="plus" size={14} />
                      </Link>
                    )}
                    {item.children && active && (
                      <ul role="list" className="nav-sub">
                        {item.children.map((c) => {
                          const current = c.current(pathname, query);
                          const n = c.count && overview ? c.count(overview) : undefined;
                          return (
                            <li key={c.to}>
                              <Link to={c.to} className="nav-sub-link" aria-current={current ? "page" : undefined}>
                                <span className="nav-label">{c.label}</span>
                                {n !== undefined && <span className="nav-sub-count">{n}</span>}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <UserMenu me={me} onSignOut={onSignOut} />
    </aside>
  );
}

/** Which part of the group this is, and the way to the others. */
function Switcher() {
  const { open, setOpen, ref } = usePopover();
  const id = useId();
  return (
    <div className="switcher" ref={ref}>
      <button type="button" className="switcher-btn" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
        <span className="switcher-tile" aria-hidden="true">
          <img src={brandAsset("mark-ivory.svg")} alt="" width={200} height={200} />
        </span>
        <span className="switcher-text">
          <span className="switcher-name">Mashriqain Ops</span>
          <span className="switcher-note">Administration</span>
        </span>
        <Icon name="updown" size={16} />
      </button>
      {open && (
        <div id={id} className="popover switcher-menu" role="menu">
          <p className="popover-label">Mashriqain group</p>
          <span className="popover-item" role="menuitem" aria-current="true">
            <span className="switcher-tile small" aria-hidden="true">
              <img src={brandAsset("mark-ivory.svg")} alt="" width={200} height={200} />
            </span>
            <span className="popover-text">
              <span>Ops</span>
              <span className="popover-note">This panel</span>
            </span>
            <Icon name="check" size={16} />
          </span>
          {GROUP_SITES.map((s) => (
            <a key={s.href} href={s.href} target="_blank" rel="noreferrer" className="popover-item" role="menuitem" onClick={() => setOpen(false)}>
              <span className="popover-icon">
                <Icon name={s.icon} size={16} />
              </span>
              <span className="popover-text">
                <span>{s.label}</span>
                <span className="popover-note">{s.note}</span>
              </span>
              <Icon name="external" size={14} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

interface Hit {
  key: string;
  to: string;
  label: string;
  note?: string;
  icon?: IconName;
  account?: Account;
}

/**
 * Find an account or a page from anywhere. Accounts come from the same
 * search the Accounts page runs; Enter on nothing in particular opens that
 * page with the search filled in.
 */
function QuickFind({ inputRef, onSearch }: { inputRef: RefObject<HTMLInputElement | null>; onSearch: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const [found, setFound] = useState<{ term: string; accounts: Account[]; total: number }>();
  const [active, setActive] = useState(-1);
  const listId = useId();
  const term = q.trim();

  useEffect(() => {
    if (!term) return;
    let live = true;
    const t = setTimeout(() => {
      get<{ accounts: Account[]; total: number }>(`/accounts?q=${encodeURIComponent(term)}`).then(
        (page) => live && setFound({ term, accounts: page.accounts.slice(0, 5), total: page.total }),
        () => live && setFound({ term, accounts: [], total: 0 }),
      );
    }, 180);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [term]);

  const lower = term.toLowerCase();
  const pages: Hit[] = term ? JUMPS.filter((j) => j.label.toLowerCase().includes(lower))
        .slice(0, 4)
        .map((j) => ({ key: "p" + j.to, ...j })) : [];
  const people: Hit[] =
    term && found?.term === term
      ? found.accounts.map((a) => ({ key: "a" + a.id, to: `/accounts/${a.id}`, label: a.name, note: identity(a), account: a }))
      : [];
  const all = term ? `/accounts?q=${encodeURIComponent(term)}` : "/accounts";
  const hits: Hit[] = [...people, ...pages];
  const showing = focused && term !== "";
  const searching = term !== "" && found?.term !== term;

  const go = (to: string) => {
    navigate(to);
    setQ("");
    setActive(-1);
    inputRef.current?.blur();
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const n = hits.length;
      if (!n) return;
      setActive((i) => (e.key === "ArrowDown" ? (i + 1) % n : (i - 1 + n) % n));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(hits[active]?.to ?? all);
    } else if (e.key === "Escape") {
      if (q) setQ("");
      else inputRef.current?.blur();
    }
  };

  return (
    <div className="quickfind">
      <button type="button" className="quickfind-icon-btn" aria-label="Search" data-tip="Search" onClick={onSearch}>
        <Icon name="search" size={18} />
      </button>
      <label className="quickfind-field">
        <Icon name="search" size={16} />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showing}
          aria-controls={listId}
          aria-activedescendant={showing && active >= 0 ? `${listId}-${active}` : undefined}
          aria-label="Find an account or a page"
          placeholder="Find an account…"
          autoComplete="off"
          spellCheck={false}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(-1);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={onKey}
        />
        <kbd className="quickfind-kbd" aria-hidden="true">
          {isMac ? "⌘K" : "Ctrl K"}
        </kbd>
      </label>
      {showing && (
        <div className="popover quickfind-menu">
          <ul id={listId} role="listbox" aria-label="Results">
            {[
              { label: "Accounts", items: people, from: 0 },
              { label: "Pages", items: pages, from: people.length },
            ].map(
              (g) =>
                g.items.length > 0 && (
                  <li key={g.label} role="presentation">
                    <p className="popover-label">{g.label}</p>
                    <ul role="presentation">
                      {g.items.map((h, j) => {
                        const i = g.from + j;
                        return (
                          <li
                            key={h.key}
                            id={`${listId}-${i}`}
                            role="option"
                            aria-selected={i === active}
                            className="popover-item"
                            // Before the input's blur, which would close the list.
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => go(h.to)}
                            onMouseEnter={() => setActive(i)}
                          >
                            {h.account ? (
                              <Avatar name={h.account.name} />
                            ) : (
                              <span className="popover-icon">
                                <Icon name={h.icon ?? "arrow"} size={16} />
                              </span>
                            )}
                            <span className="popover-text">
                              <span>{h.label}</span>
                              {h.note && <span className="popover-note">{h.note}</span>}
                            </span>
                            {h.account?.role === "admin" && <span className="popover-tag">Admin</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ),
            )}
          </ul>
          {searching && people.length === 0 && <p className="popover-empty">Searching…</p>}
          {!searching && hits.length === 0 && <p className="popover-empty">No account or page matches “{term}”.</p>}
          <button type="button" className="popover-foot" onMouseDown={(e) => e.preventDefault()} onClick={() => go(all)}>
            {found?.term === term && found.total > people.length ? `See all ${found.total} accounts` : "Search in Accounts"}
            <Icon name="arrow" size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

/** The signed-in administrator, and the way out. */
function UserMenu({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const { open, setOpen, ref } = usePopover();
  const id = useId();
  const a = me.account;
  return (
    <div className="sidebar-foot" ref={ref}>
      <button type="button" className="me" aria-haspopup="menu" aria-expanded={open} aria-controls={id} data-tip={a.name} onClick={() => setOpen(!open)}>
        <span className="me-avatar">
          <Avatar name={a.name} />
          <span className="me-online" aria-hidden="true" />
        </span>
        <span className="me-text">
          <span className="me-name">{a.name}</span>
          <span className="me-id">{identity(a)}</span>
        </span>
        <Icon name="updown" size={16} />
      </button>
      {open && (
        <div id={id} className="popover user-menu" role="menu">
          <div className="user-menu-head">
            <Avatar name={a.name} large />
            <span className="popover-text">
              <span className="user-menu-name">{a.name}</span>
              <span className="popover-note">{identity(a)}</span>
            </span>
          </div>
          <p className="user-menu-role">
            <Icon name="shield" size={14} />
            Administrator
          </p>
          <div className="popover-sep" role="separator" />
          <Link to={`/accounts/${a.id}`} className="popover-item" role="menuitem" onClick={() => setOpen(false)}>
            <span className="popover-icon">
              <Icon name="user" size={16} />
            </span>
            <span className="popover-text">Your account here</span>
          </Link>
          <a href="https://id.mashriqain.com/" target="_blank" rel="noreferrer" className="popover-item" role="menuitem" onClick={() => setOpen(false)}>
            <span className="popover-icon">
              <Icon name="key" size={16} />
            </span>
            <span className="popover-text">Password and two-step</span>
            <Icon name="external" size={14} />
          </a>
          <div className="popover-sep" role="separator" />
          <button type="button" className="popover-item danger" role="menuitem" onClick={onSignOut}>
            <span className="popover-icon">
              <Icon name="signout" size={16} />
            </span>
            <span className="popover-text">Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
